import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, XCircle, Undo2, History, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCalloutFeedback } from "@/lib/calloutFeedback";

const VERDICT_STYLE = {
  correct: { label: "Marked correct", klass: "text-emerald-300 border-emerald-700/60 bg-emerald-950/30", Icon: ThumbsUp },
  wrong: { label: "Marked wrong", klass: "text-rose-300 border-rose-700/60 bg-rose-950/30", Icon: ThumbsDown },
  dismissed: { label: "Dismissed", klass: "text-muted-foreground border-border bg-background/40", Icon: XCircle },
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

function PriorNotes({ items, testId }) {
  if (!items?.length) return null;
  return (
    <div className="mt-2 rounded border border-amber-800/50 bg-amber-950/20 p-2 text-xs space-y-1" data-testid={testId}>
      <p className="flex items-center gap-1.5 text-amber-300 font-medium">
        <History className="w-3.5 h-3.5" />
        Testers rejected this callout in {items.length} earlier analys{items.length === 1 ? "is" : "es"}
      </p>
      {items.map((p) => (
        <p key={p.id} className="text-muted-foreground">
          <span className="text-foreground/80">{p.verdict === "wrong" ? "Wrong" : "Dismissed"}:</span>{" "}
          “{p.reason || "no reason given"}” <span className="opacity-70">— on “{(p.proposed_text || "").slice(0, 70)}…” · {fmtDate(p.updated_at)}</span>
        </p>
      ))}
    </div>
  );
}

function ReasonForm({ verdict, onSave, onCancel, busy, testId }) {
  const [reason, setReason] = useState("");
  const required = verdict === "dismissed";
  return (
    <div className="mt-2 space-y-1.5" data-testid={`${testId}-reason-form`}>
      <Textarea
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={required ? "Why isn't this relevant? (required — the LLM sees this next time)" : "What's wrong with this callout? (optional)"}
        className="min-h-[60px] text-sm"
        data-testid={`${testId}-reason-input`}
      />
      <div className="flex gap-1.5">
        <Button size="sm" disabled={busy || (required && !reason.trim())} onClick={() => onSave(reason.trim())} data-testid={`${testId}-reason-save`}>
          {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
          {required ? "Dismiss callout" : "Mark wrong"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`${testId}-reason-cancel`}>Cancel</Button>
      </div>
    </div>
  );
}

/** Correct / Wrong / Dismiss controls for one LLM callout. */
export function CalloutFeedback({ kind, targetKey, label, callout, testId }) {
  const ctx = useCalloutFeedback();
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!ctx) return null;
  const current = ctx.mine[targetKey];
  const prior = ctx.prior[targetKey];

  const send = async (verdict, reason = "") => {
    setBusy(true);
    setError(null);
    try {
      await ctx.submit({ kind, targetKey, verdict, reason, label, callout });
      setPending(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };
  const undo = async () => {
    setBusy(true);
    try { await ctx.remove(targetKey); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid={testId} onClick={(e) => e.stopPropagation()}>
      {current ? (
        <div className="flex items-start gap-2 flex-wrap text-xs">
          {(() => {
            const v = VERDICT_STYLE[current.verdict];
            return (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${v.klass}`} data-testid={`${testId}-verdict`}>
                <v.Icon className="w-3 h-3" /> {v.label}
              </span>
            );
          })()}
          {current.reason && <span className="text-muted-foreground italic flex-1 min-w-[160px]" data-testid={`${testId}-verdict-reason`}>“{current.reason}”</span>}
          <button type="button" onClick={undo} disabled={busy} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground" data-testid={`${testId}-undo`}>
            <Undo2 className="w-3 h-3" /> Undo
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[11px] text-muted-foreground mr-1">Was this callout right?</span>
          <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-emerald-300" disabled={busy} onClick={() => send("correct")} data-testid={`${testId}-correct`}>
            <ThumbsUp className="w-3 h-3 mr-1" /> Correct
          </Button>
          <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-rose-300" disabled={busy} onClick={() => setPending("wrong")} data-testid={`${testId}-wrong`}>
            <ThumbsDown className="w-3 h-3 mr-1" /> Wrong
          </Button>
          <Button size="sm" variant="outline" className="h-6 px-2 text-xs" disabled={busy} onClick={() => setPending("dismissed")} data-testid={`${testId}-dismiss`}>
            <XCircle className="w-3 h-3 mr-1" /> Dismiss
          </Button>
        </div>
      )}
      {pending && !current && (
        <ReasonForm key={pending} verdict={pending} busy={busy} onSave={(r) => send(pending, r)} onCancel={() => setPending(null)} testId={testId} />
      )}
      {error && <p className="text-xs text-rose-300 mt-1" data-testid={`${testId}-error`}>{error}</p>}
      <PriorNotes items={prior} testId={`${testId}-prior`} />
    </div>
  );
}
